import { lazy, Suspense, useEffect, useState } from "react";
import PublicNav from "@/components/PublicNav";
import SiteFooter from "@/components/SiteFooter";
import PromoBanner from "@/components/PromoBanner";
import { SignupBonusPromo } from "@/components/SignupBonus";
import ExchangeCalculator from "@/components/ExchangeCalculator";
import HeroFloatingCards from "@/components/HeroFloatingCards";
import SplineBackground from "@/components/SplineBackground";
import GiftCardIcon from "@/components/GiftCardIcon";
import { useSettings } from "@/context/SettingsContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import GoogleReviews, { useGoogleReviews } from "@/components/GoogleReviews";
import { Reveal, FeaturesGrid, HowItWorks, StatsStrip, RatesChart, Mascot } from "@/components/HomeVisuals";
import { HeroBuddy, WaveBuddy, RatesBuddy, ThinkBuddy, CelebrateBuddy, FootballBuddy } from "@/components/Buddy";
import {
    PlayCircle, Wallet, TrendingUp, Zap, ShieldCheck, LifeBuoy,
    ArrowRight, Star, Smartphone, Tag,
} from "lucide-react";

// Lazy load heavy below-fold components
const LiveFeedTicker = lazy(() => import("@/components/LiveFeedTicker"));

const FEATURES = [
    { icon: Wallet, title: "1000+ Gift Cards", desc: "Wide range of gift cards supported", tone: "bg-indigo-50 text-indigo-500" },
    { icon: TrendingUp, title: "Best Exchange Rates", desc: "Get the most value for your cards", tone: "bg-amber-50 text-amber-500" },
    { icon: Zap, title: "Instant Payments", desc: "Receive your cash instantly", tone: "bg-emerald-50 text-emerald-500" },
    { icon: ShieldCheck, title: "Secure & Safe", desc: "Your transactions are 100% secure", tone: "bg-orange-50 text-orange-500" },
    { icon: LifeBuoy, title: "24/7 Support", desc: "Our support team is always here to help", tone: "bg-violet-50 text-violet-500" },
];

// Avatar images preloaded with fixed dimensions to prevent CLS
const HERO_AVATARS = [
    "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=120&auto=format",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=120&auto=format",
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=120&auto=format",
];

export default function Landing() {
    const greviews = useGoogleReviews();
    const { t } = useSettings();
    const [liveCards, setLiveCards] = useState([]);

    // Defer rates API call — not needed for LCP
    useEffect(() => {
        const timer = setTimeout(() => {
            api.get("/giftcards").then((r) => setLiveCards(r.data || [])).catch(() => {});
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const STEPS = [
        { n: "01", title: t("step_1_title"), desc: t("step_1_desc") },
        { n: "02", title: t("step_2_title"), desc: t("step_2_desc") },
        { n: "03", title: t("step_3_title"), desc: t("step_3_desc") },
    ];
    const FAQS = [
        { q: t("faq_1_q"), a: t("faq_1_a") },
        { q: t("faq_2_q"), a: t("faq_2_a") },
        { q: t("faq_3_q"), a: t("faq_3_a") },
        { q: t("faq_4_q"), a: t("faq_4_a") },
        { q: t("faq_5_q"), a: t("faq_5_a") },
        { q: t("faq_6_q"), a: t("faq_6_a") },
        { q: t("faq_7_q"), a: t("faq_7_a") },
        { q: t("faq_8_q"), a: t("faq_8_a") },
        { q: t("faq_9_q"), a: t("faq_9_a") },
        { q: t("faq_10_q"), a: t("faq_10_a") },
        { q: t("faq_11_q"), a: t("faq_11_a") },
        { q: t("faq_12_q"), a: t("faq_12_a") },
    ].filter(f => f.q && !f.q.startsWith("faq_") && f.a && !f.a.startsWith("faq_"));

    return (
        <div className="min-h-screen bg-cream relative overflow-hidden">
            <PublicNav />
            <PromoBanner />

            {/* HERO */}
            <section className="relative">
                <div aria-hidden className="absolute inset-0 -z-10"
                    style={{ background: "radial-gradient(1200px 600px at 80% 0%, rgba(251,191,36,0.18), transparent 60%), radial-gradient(900px 500px at 0% 30%, rgba(251,191,36,0.08), transparent 60%), linear-gradient(180deg, #FDFBF7 0%, #FFFFFF 100%)" }} />
                <SplineBackground />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-20 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 bg-white border border-slate-100 px-4 py-1.5 rounded-full shadow-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                            <span className="text-xs font-semibold text-slate-700">{t("hero_pill")}</span>
                        </div>
                        <h1 className="text-balance text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-slate-900 leading-[1.05]">
                            {t("hero_title_pre")} <span className="block">{t("hero_title_main")}</span>
                            {t("hero_title_post")} <span className="text-brand">{t("hero_title_highlight")}</span>
                        </h1>
                        <p className="text-lg text-slate-600 max-w-md leading-relaxed">{t("hero_subtitle")}</p>
                        <SignupBonusPromo />
                        <div className="flex flex-wrap items-center gap-3">
                            <Link to="/register" data-testid="hero-exchange-now-button"
                                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-500 text-white font-semibold px-7 py-3.5 rounded-full shadow-button transition-all hover:-translate-y-0.5">
                                {t("hero_cta_primary")} <ArrowRight size={16} />
                            </Link>
                            <a href="#how-it-works" data-testid="hero-how-it-works-button"
                                className="inline-flex items-center gap-2 bg-white text-slate-800 font-semibold px-6 py-3.5 rounded-full border border-slate-200 hover:border-slate-300 transition-all">
                                <PlayCircle size={18} className="text-brand" />
                                {t("hero_cta_secondary")}
                            </a>
                        </div>

                        {/* Fixed dimensions on avatars to prevent CLS */}
                        <div className="flex items-center gap-3 pt-3">
                            <div className="flex -space-x-2" style={{ width: 84, height: 36 }}>
                                {HERO_AVATARS.map((src) => (
                                    <img key={src} src={src} alt="" width={36} height={36}
                                        className="w-9 h-9 rounded-full border-2 border-white object-cover"
                                        loading="eager" fetchpriority="low" />
                                ))}
                            </div>
                            <div className="text-sm">
                                <div className="font-semibold text-slate-800">Trusted by 10,000+ users worldwide</div>
                                <div className="flex items-center gap-1 text-slate-500 text-xs">
                                    {greviews?.enabled && greviews.rating
                                        ? <a href="#reviews" className="hover:underline">{Number(greviews.rating).toFixed(1)} out of 5 on Google ({greviews.count} reviews)</a>
                                        : "4.8 out of 5"}
                                    <span className="flex text-emerald-500">
                                        {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={12} fill="currentColor" stroke="none" />)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative flex justify-center lg:justify-end min-h-[560px]">
                        <div className="absolute inset-0 hidden lg:block">
                            <HeroFloatingCards />
                        </div>
                        <div className="relative z-20">
                            <ExchangeCalculator />
                        </div>
                        {/* cartoon buddy watching the calculator (wide screens) */}
                        <HeroBuddy className="hidden xl:block absolute bottom-4 right-[440px] z-30 pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="gift-cards" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-20">
                <FeaturesGrid features={FEATURES} />
            </section>

            {/* HOW IT WORKS */}
            <section id="how-it-works" className="bg-white py-20 border-y border-slate-100">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <WaveBuddy className="hidden lg:block absolute left-4 xl:left-12 -top-4" size={105} />
                    <Reveal className="text-center mb-14">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-500">{t("steps_eyebrow")}</div>
                        <h2 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mt-3">{t("steps_title")}</h2>
                        <p className="text-slate-600 mt-3 max-w-xl mx-auto">{t("steps_subtitle")}</p>
                    </Reveal>
                    <HowItWorks steps={STEPS} cards={liveCards} />
                </div>
            </section>

            {/* RATES TEASER */}
            <section id="rates" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="grid lg:grid-cols-3 gap-6 items-stretch">
                    <Reveal className="lg:col-span-1">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-500">Live Rates</div>
                        <h2 className="text-4xl font-heading font-bold text-slate-900 mt-3">Best rates in the market</h2>
                        <p className="text-slate-600 mt-3">We continuously monitor rates so you always get the most for your cards.</p>
                        <Link to="/register" className="mt-6 inline-flex items-center gap-2 text-brand-500 font-semibold">
                            Start exchanging <ArrowRight size={16} />
                        </Link>
                        <RatesBuddy className="hidden lg:block mt-10 ml-6" />
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-2">
                        <RatesChart cards={liveCards} />
                    </Reveal>
                </div>
            </section>


            {/* STATS + PAYOUT STRIP */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                <Reveal><StatsStrip cardCount={liveCards.length} /></Reveal>
            </section>

            {/* MARKETPLACE PROMO */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Reveal className="relative rounded-3xl overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #1a0533 50%, #0d0d2b 100%)" }}>
                    <div aria-hidden className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20"
                            style={{ background: "radial-gradient(ellipse, #7c3aed, transparent 70%)" }} />
                        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10"
                            style={{ background: "radial-gradient(ellipse, #ec4899, transparent 70%)" }} />
                        <div className="absolute inset-0 opacity-5"
                            style={{ backgroundImage: "linear-gradient(rgba(139,92,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.5) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                    </div>
                    <div className="relative z-10 p-8 lg:p-12">
                        <div className="grid lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/30 px-4 py-1.5 rounded-full mb-5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                                    <span className="text-xs font-semibold text-violet-300">New Feature — P2P Marketplace</span>
                                </div>
                                <h2 className="text-3xl lg:text-4xl font-heading font-black text-white leading-tight mb-4">
                                    Buy Gift Cards from Other Users at
                                    <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent"> Better Prices</span>
                                </h2>
                                <p className="text-violet-200/70 leading-relaxed mb-6">
                                    Our P2P marketplace connects buyers and sellers directly. Get gift cards below face value, or sell yours for more cash than any exchange offers. Every deal is escrow-protected and admin-verified.
                                </p>
                                <div className="grid grid-cols-2 gap-3 mb-8">
                                    {[
                                        { icon: "🔒", title: "Escrow Protected", desc: "Funds & codes held safely" },
                                        { icon: "✅", title: "KYC Verified", desc: "All users identity verified" },
                                        { icon: "💬", title: "Built-in Chat", desc: "Talk to buyers & sellers" },
                                        { icon: "💸", title: "Only 5% Fee", desc: "Sellers pay on completion" },
                                    ].map((f, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <span className="text-lg">{f.icon}</span>
                                            <div>
                                                <div className="text-white text-xs font-semibold">{f.title}</div>
                                                <div className="text-violet-300/60 text-xs">{f.desc}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <Link to="/marketplace"
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-violet-500/30 transition-all hover:-translate-y-0.5">
                                        Browse Marketplace <ArrowRight size={16} />
                                    </Link>
                                    <Link to="/register"
                                        className="inline-flex items-center gap-2 bg-white/10 border border-white/20 hover:bg-white/15 text-white font-semibold px-6 py-3.5 rounded-full transition-all">
                                        Sell a Card
                                    </Link>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { card: "Amazon Gift Card", face: 100, asking: 82, badge: "official", savings: 18 },
                                    { card: "Xbox Gift Card", face: 50, asking: 41, badge: "verified", savings: 18 },
                                    { card: "iTunes Gift Card", face: 200, asking: 160, badge: "verified", savings: 20 },
                                ].map((l, i) => (
                                    <Link key={i} to="/marketplace"
                                        className="flex items-center justify-between bg-white/5 border border-white/10 hover:bg-white/10 hover:border-violet-500/30 rounded-2xl p-4 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                                                <Tag size={16} className="text-white" />
                                            </div>
                                            <div>
                                                <div className="text-white font-semibold text-sm">{l.card}</div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    {l.badge === "official" ? (
                                                        <span className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-bold">🔵 Official</span>
                                                    ) : (
                                                        <span className="text-xs bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold">✓ Verified</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-white/50 text-xs line-through">${l.face}</div>
                                            <div className="text-fuchsia-400 font-bold">${l.asking}</div>
                                            <div className="text-xs text-violet-300/60">{l.savings}% off</div>
                                        </div>
                                    </Link>
                                ))}
                                <Link to="/marketplace" className="flex items-center justify-center gap-2 text-violet-300 hover:text-white text-sm font-semibold py-3 transition-colors">
                                    View all listings <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* REAL GOOGLE REVIEWS (hidden until Google is connected) */}
            <GoogleReviews />

            {/* FAQ */}
            <section id="faq" className="bg-white py-20 border-t border-slate-100">
                <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ThinkBuddy className="hidden xl:block absolute right-full mr-20 top-16" />
                    <Reveal className="text-center mb-10">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-500">{t("faq_eyebrow")}</div>
                        <h2 className="text-4xl font-heading font-bold text-slate-900 mt-3">{t("faq_title")}</h2>
                    </Reveal>
                    <div className="space-y-3">
                        {FAQS.map((f, i) => (
                            <Reveal key={i} delay={Math.min(i, 6) * 60} y={16}>
                            <details className="group bg-cream rounded-2xl border border-slate-100 px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
                                <summary className="flex justify-between items-center cursor-pointer text-slate-900 font-medium">
                                    <span>{f.q}</span>
                                    <span className="text-brand text-2xl group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <p className="text-slate-600 text-sm mt-3 leading-relaxed">{f.a}</p>
                            </details>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* GET THE APP */}
            <section id="get-app" className="py-20 bg-cream border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal className="rounded-3xl bg-navy relative overflow-hidden p-6 sm:p-10 lg:p-14">
                        <div aria-hidden className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brand/20 blur-3xl" />
                        <div aria-hidden className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
                        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-1.5 rounded-full mb-4">
                                    <Smartphone size={14} className="text-brand" />
                                    <span className="text-xs font-semibold text-white">Available on all devices</span>
                                </div>
                                <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white leading-tight">
                                    Get the GiftToCash app on your phone
                                </h2>
                                <p className="mt-4 text-slate-300 max-w-md">
                                    Install GiftToCash directly on your home screen - no app store needed. Works on Android and iPhone.
                                </p>
                                <div className="mt-6 grid grid-cols-2 gap-3">
                                    {[
                                        { icon: "⚡", text: "Instant access" },
                                        { icon: "📱", text: "Works offline" },
                                        { icon: "🔔", text: "Push notifications" },
                                        { icon: "🆓", text: "Completely free" },
                                    ].map((b) => (
                                        <div key={b.text} className="flex items-center gap-2 text-sm text-slate-300">
                                            <span>{b.icon}</span><span>{b.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="bg-white/10 border border-white/20 rounded-2xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.523 15.341c-.302 0-.546-.244-.546-.546s.244-.546.546-.546.546.244.546.546-.244.546-.546.546m-11.046 0c-.302 0-.546-.244-.546-.546s.244-.546.546-.546.546.244.546.546-.244.546-.546.546m11.405-6.765l1.079-1.868a.224.224 0 0 0-.082-.306.224.224 0 0 0-.306.082l-1.093 1.893A6.579 6.579 0 0 0 12 7.748a6.579 6.579 0 0 0-5.48 2.629L5.427 8.484a.224.224 0 0 0-.306-.082.224.224 0 0 0-.082.306l1.079 1.868C4.343 11.66 3.293 13.554 3 15.68h18c-.293-2.126-1.343-4.02-3.118-5.104"/></svg>
                                        </div>
                                        <span className="text-white font-semibold">Android (Chrome)</span>
                                    </div>
                                    <div className="space-y-2.5">
                                        {["Open gifttocash.com in Chrome","Tap the menu in the top right","Tap Add to Home screen","Tap Add to confirm"].map((step, i) => (
                                            <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                                <span className="w-6 h-6 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                                                {step}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-white/10 border border-white/20 rounded-2xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                                        </div>
                                        <span className="text-white font-semibold">iPhone (Safari)</span>
                                    </div>
                                    <div className="space-y-2.5">
                                        {["Open gifttocash.com in Safari","Tap the Share button at the bottom","Scroll down and tap Add to Home Screen","Tap Add to confirm"].map((step, i) => (
                                            <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                                <span className="w-6 h-6 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                                                {step}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <a href="https://gifttocash.com"
                                    className="block text-center bg-brand hover:bg-brand-500 text-white font-semibold py-3.5 rounded-full shadow-button transition-all">
                                    Open GiftToCash Now
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <Reveal className="rounded-3xl bg-navy relative overflow-hidden p-6 sm:p-10 lg:p-14 text-white">
                    <div aria-hidden className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brand/20 blur-3xl" />
                    <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-center">
                        <div className="order-2 lg:order-1 text-center lg:text-left">
                            <h2 className="text-4xl lg:text-5xl font-heading font-bold leading-tight">{t("cta_title")}</h2>
                            <p className="mt-4 text-slate-300 max-w-md mx-auto lg:mx-0">{t("cta_subtitle")}</p>
                            <div className="mt-7 flex flex-wrap gap-3 justify-center lg:justify-start">
                                <Link to="/register" className="bg-brand hover:bg-brand-500 text-white font-semibold px-7 py-3.5 rounded-full shadow-button">{t("cta_primary")}</Link>
                                <Link to="/login" className="bg-white/10 border border-white/20 hover:bg-white/15 text-white font-semibold px-6 py-3.5 rounded-full">{t("cta_secondary")}</Link>
                            </div>
                        </div>
                        <Mascot className="order-1 lg:order-2 mx-auto w-48 h-64 sm:w-56 sm:h-72 lg:w-72 lg:h-[26rem] -my-2 lg:-my-10"
                            fallback={<CelebrateBuddy className="order-1 lg:order-2 mx-auto mt-6 lg:mt-10" size={150} />} />
                    </div>
                </Reveal>
            </section>

            <SiteFooter extra={<FootballBuddy className="mt-8 ml-2" />} />

            {/* Lazy loaded below-fold components */}
            <Suspense fallback={null}>
                <LiveFeedTicker />
            </Suspense>
        </div>
    );
}